/**
 * The browser's only way to reach a server.
 *
 * Every request in this application goes through here, to one relative path on
 * its own origin. That is deliberate and load-bearing:
 *
 * * **No third-party service is ever called from the browser.** Not Photon, not
 *   Nominatim, not OpenTripPlanner. Steps 6 and 8 put caching, rate discipline,
 *   provider policy, normalization and the privacy posture in the backend, and
 *   none of it can be enforced from a page.
 * * **Relative paths only.** In development Vite proxies `/api`; in Compose the
 *   web container's Caddy does; in production both are served from one origin.
 *   The bundle never learns a hostname.
 *
 * The one exception is map tiles, which by their nature are fetched by the
 * browser from the configured tile provider. That is documented in
 * `docs/step-9-map-selection-interface.md` under privacy.
 */

/** A failure that carries the backend's own error code, when it sent one. */
export class ApiError extends Error {
  readonly status: number;
  /** The taxonomy code from Step 6 or Step 8, or `null` for a transport failure. */
  readonly code: string | null;
  /** The parsed error body, so a caller can read `nearestOverLimit`. */
  readonly body: unknown;

  constructor(status: number, code: string | null, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** Raised when a request was deliberately superseded. Never shown to a user. */
export class RequestCancelledError extends Error {
  constructor() {
    super('The request was cancelled.');
    this.name = 'RequestCancelledError';
  }
}

interface ErrorEnvelope {
  error?: { code?: unknown; message?: unknown };
}

/**
 * POSTs JSON to one of our endpoints and returns the parsed body.
 *
 * POST, not GET, throughout: a place query and a journey request both describe
 * where somebody lives or intends to go, and a query string puts that in access
 * logs, proxy logs, browser history and `Referer` headers. The backend made the
 * same choice for the same reason.
 */
export async function postJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (error) {
    if (isAbort(error)) throw new RequestCancelledError();
    throw new ApiError(0, null, 'The service could not be reached.', null);
  }

  return readResponse<T>(response);
}

export async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      headers: { accept: 'application/json' },
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (error) {
    if (isAbort(error)) throw new RequestCancelledError();
    throw new ApiError(0, null, 'The service could not be reached.', null);
  }

  return readResponse<T>(response);
}

async function readResponse<T>(response: Response): Promise<T> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // A proxy answering with HTML is a transport failure, not a contract one.
    if (response.ok) {
      throw new ApiError(response.status, null, 'The service returned an unreadable answer.', null);
    }
  }

  if (response.ok) return payload as T;

  const envelope = payload as ErrorEnvelope | null;
  const code = typeof envelope?.error?.code === 'string' ? envelope.error.code : null;
  const message =
    typeof envelope?.error?.message === 'string'
      ? envelope.error.message
      : 'The request could not be completed.';

  throw new ApiError(response.status, code, message, payload);
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && (error.name === 'AbortError' || error.name === 'TimeoutError');
}
